import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EmptyState } from '@pazportal/ui';
import { supabase } from '../lib/supabase';
import { StatusBadge } from '../components/StatusBadge';
import { TicketConversation } from '../components/TicketConversation';
import { SkeletonCard, SkeletonRow } from '../components/Skeleton';
import { formatDateTime, formatDate, formatSolutionType, timeAgo } from '../lib/formatters';
import { useActivityFeed } from '../hooks/useActivityFeed';
import { TICKET_SELECT, type TicketRecord } from '../hooks/useTickets';
import { useCustomerScope } from '../contexts/AccountContext';
import { useI18n } from '../i18n/I18nProvider';
import type { MessageKey } from '../i18n/messages';

/**
 * One ticket, scoped to the current account selection (portfolio → any
 * linked account). Staff identity never renders: the select list has no
 * assignee join and the timeline comes from customer_activity_feed, which
 * reports an actor KIND rather than a name.
 */
function useTicket(id: string | undefined) {
  const scope = useCustomerScope();
  return useQuery({
    queryKey: ['customer-ticket', scope.key, id],
    enabled: Boolean(id) && scope.ready && scope.ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tickets_customer_view')
        .select(TICKET_SELECT)
        .eq('id', id!)
        .in('customer_id', scope.ids)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as TicketRecord | null) ?? null;
    },
    staleTime: 30 * 1000,
  });
}

const ACTOR_KEY: Record<string, MessageKey> = {
  system: 'dashboard.actor.system',
  paznetworks: 'dashboard.actor.paznetworks',
  customer: 'dashboard.actor.customer',
};

export function TicketDetailPage() {
  const { t } = useI18n();
  const { id, ticketId } = useParams<{ id?: string; ticketId?: string }>();
  const ticketKey = ticketId ?? id;
  const ticketsHref = ticketId && id ? `/locations/${id}/tickets` : '/tickets';
  const navigate = useNavigate();
  const scope = useCustomerScope();
  const { data: ticket, isLoading, isError, error } = useTicket(ticketKey);
  const activity = useActivityFeed({
    customerId: ticket?.customer_id ?? null,
    limit: 100,
    enabled: !!ticket,
  });

  if (!scope.ready || isLoading) {
    return (
      <div className="space-y-4">
        <SkeletonCard />
        <SkeletonRow />
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <div className="rounded-lg border border-border bg-surface">
        <EmptyState
          icon={<TicketIcon />}
          title={t('tickets.detail.not_found')}
          subtitle={isError && error instanceof Error ? error.message : t('tickets.detail.not_found_hint')}
          action={{ label: t('tickets.detail.back'), onClick: () => navigate(ticketsHref) }}
        />
      </div>
    );
  }

  const ticketEvents = (activity.data?.events ?? []).filter(
    (e) => e.entity_type === 'tickets' && e.entity_id === ticket.id,
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(ticketsHref)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-md text-text-faint hover:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={t('tickets.detail.back')}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="min-w-0">
          <p className="font-mono text-xs text-text-faint">{ticket.ticket_number}</p>
          <h1 className="break-words text-xl font-semibold text-text">{ticket.title}</h1>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={ticket.status ?? 'open'} />
        <StatusBadge status={ticket.priority ?? 'medium'} />
        {ticket.source && (
          <span className="text-xs text-text-muted">{t('tickets.detail.via', { source: ticket.source })}</span>
        )}
        {ticket.sla_deadline && !ticket.resolved_at && (
          <span className="text-xs text-warning">
            {t('tickets.detail.sla', { date: formatDateTime(ticket.sla_deadline) })}
          </span>
        )}
        {ticket.resolved_at && (
          <span className="text-xs text-success">
            {t('tickets.resolved_ago', { ago: timeAgo(ticket.resolved_at) })}
          </span>
        )}
      </div>

      {ticket.description && (
        <section className="rounded-lg border border-border bg-surface p-4 sm:p-5">
          <h2 className="mb-2 text-sm font-medium text-text-muted">{t('tickets.detail.description')}</h2>
          <p className="whitespace-pre-wrap break-words text-sm text-text">{ticket.description}</p>
        </section>
      )}

      <section className="rounded-lg border border-border bg-surface p-4 sm:p-5">
        <h2 className="mb-3 text-sm font-medium text-text-muted">{t('tickets.detail.context')}</h2>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div className="flex justify-between gap-3">
            <dt className="text-text-muted">{t('tickets.detail.created')}</dt>
            <dd className="text-text">{formatDate(ticket.created_at)}</dd>
          </div>
          {ticket.customer && (
            <div className="flex justify-between gap-3">
              <dt className="text-text-muted">{t('tickets.detail.account')}</dt>
              <dd className="truncate text-text">{ticket.customer.name}</dd>
            </div>
          )}
          {ticket.location && (
            <div className="flex justify-between gap-3">
              <dt className="text-text-muted">{t('tickets.detail.location')}</dt>
              <dd>
                <Link to={`/locations/${ticket.location.id}`} className="truncate text-primary hover:underline">
                  {ticket.location.name}
                </Link>
              </dd>
            </div>
          )}
          {ticket.deployment && (
            <div className="flex justify-between gap-3">
              <dt className="text-text-muted">{t('tickets.detail.system')}</dt>
              <dd className="text-text">{formatSolutionType(ticket.deployment.solution_type)}</dd>
            </div>
          )}
          <div className="flex justify-between gap-3">
            <dt className="text-text-muted">{t('tickets.detail.last_update')}</dt>
            <dd className="text-text">{formatDateTime(ticket.updated_at)}</dd>
          </div>
        </dl>
      </section>

      {ticket.resolved_at && ticket.resolution_notes && (
        <section className="rounded-lg border border-success/30 bg-success-soft/30 p-4 sm:p-5">
          <h2 className="mb-2 text-sm font-medium text-success">{t('tickets.detail.resolution')}</h2>
          <p className="whitespace-pre-wrap text-sm text-text">{ticket.resolution_notes}</p>
        </section>
      )}

      <section className="rounded-lg border border-border bg-surface p-4 sm:p-5">
        <h2 className="mb-3 text-sm font-medium text-text-muted">{t('tickets.detail.timeline')}</h2>
        {activity.isLoading ? (
          <SkeletonRow />
        ) : activity.isError ? (
          <p role="alert" className="text-sm text-notification">
            {t('tickets.detail.timeline_error')}{' '}
            {activity.error instanceof Error ? activity.error.message : ''}
          </p>
        ) : ticketEvents.length === 0 ? (
          <p className="text-sm text-text-faint">{t('tickets.detail.timeline_empty')}</p>
        ) : (
          <ol className="space-y-2">
            {ticketEvents.map((e) => (
              <li key={e.id} className="flex items-start gap-3 text-sm">
                <span aria-hidden="true" className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                <div className="min-w-0 flex-1">
                  <p className="text-text">{e.summary}</p>
                  <p className="text-xs text-text-faint">
                    {formatDateTime(e.occurred_at)} · {t(ACTOR_KEY[e.actor_kind] ?? 'dashboard.actor.system')}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <TicketConversation ticketId={ticket.id} customerId={ticket.customer_id} />
    </div>
  );
}

function TicketIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}
